import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { tasksApi, CreateTaskDto, UpdateTaskDto, Task, OutdoorTasksForVisitsResponse } from '@/api/tasks';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null) {
    const e = error as { response?: { data?: { message?: string } } };
    return e.response?.data?.message || 'Произошла ошибка';
  }
  return 'Произошла ошибка';
}

export function useListTasks(ownershipFilter?: string) {
  return useQuery({
    queryKey: ['tasks', 'list', ownershipFilter],
    queryFn: () => tasksApi.getListTasks(ownershipFilter),
    staleTime: 60_000,
  });
}

export function useClientTasks(clientId: number) {
  return useQuery({
    queryKey: ['tasks', 'client', clientId],
    queryFn: () => tasksApi.getClientTasks(clientId),
    staleTime: 60_000,
    enabled: !!clientId,
  });
}

/**
 * Fetch all outdoor tasks for visits page in one request.
 * Tasks are grouped by clientId for easy lookup.
 */
export function useOutdoorTasksForVisits(ownershipFilter?: string, enabled = true) {
  return useQuery<OutdoorTasksForVisitsResponse>({
    queryKey: ['tasks', 'outdoor-for-visits', ownershipFilter],
    queryFn: () => tasksApi.getOutdoorTasksForVisits(ownershipFilter),
    staleTime: 60_000,
    enabled,
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateTaskDto) => tasksApi.createTask(dto),
    onSuccess: (newTask) => {
      // Add to list cache optimistically
      queryClient.setQueryData<Task[]>(['tasks', 'list'], (old) =>
        old ? [newTask, ...old] : [newTask]
      );
      // Add to client cache if it's an INDOOR task
      if (newTask.clientId) {
        queryClient.setQueryData<Task[]>(
          ['tasks', 'client', newTask.clientId],
          (old) => (old ? [newTask, ...old] : [newTask])
        );
      }
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Задача создана');
    },
    onError: (error) => {
      toast.error('Ошибка создания задачи', { description: getErrorMessage(error) });
    },
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateTaskDto }) =>
      tasksApi.updateTask(id, updates),
    onMutate: async ({ id, updates }) => {
      // Cancel ongoing refetches
      await queryClient.cancelQueries({ queryKey: ['tasks'] });

      // Snapshot previous values
      const previousList = queryClient.getQueryData<Task[]>(['tasks', 'list']);

      // Optimistically update the task in list
      queryClient.setQueryData<Task[]>(['tasks', 'list'], (old) =>
        old?.map((t) => (t.id === id ? { ...t, ...updates } : t))
      );

      return { previousList };
    },
    onError: (error, _vars, context) => {
      // Rollback on error
      if (context?.previousList) {
        queryClient.setQueryData(['tasks', 'list'], context.previousList);
      }
      toast.error('Ошибка обновления', { description: getErrorMessage(error) });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Задача обновлена');
    },
  });
}

export function useCompleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tasksApi.completeTask(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['tasks'] });

      const previousList = queryClient.getQueryData<Task[]>(['tasks', 'list']);

      // Optimistically mark as completed
      queryClient.setQueryData<Task[]>(['tasks', 'list'], (old) =>
        old?.map((t) =>
          t.id === id
            ? { ...t, status: 'COMPLETED' as const, completedAt: new Date().toISOString() }
            : t
        )
      );

      // Also update client tasks cache
      queryClient.setQueriesData<Task[]>(
        { queryKey: ['tasks', 'client'] },
        (old) =>
          old?.map((t) =>
            t.id === id
              ? { ...t, status: 'COMPLETED' as const, completedAt: new Date().toISOString() }
              : t
          )
      );

      return { previousList };
    },
    onError: (error, _id, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(['tasks', 'list'], context.previousList);
      }
      toast.error('Ошибка выполнения', { description: getErrorMessage(error) });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Задача выполнена');
    },
  });
}

export function useCancelTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tasksApi.cancelTask(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['tasks'] });

      const previousList = queryClient.getQueryData<Task[]>(['tasks', 'list']);

      // Optimistically mark as cancelled
      queryClient.setQueryData<Task[]>(['tasks', 'list'], (old) =>
        old?.map((t) =>
          t.id === id ? { ...t, status: 'CANCELLED' as const } : t
        )
      );

      // Also update client tasks cache
      queryClient.setQueriesData<Task[]>(
        { queryKey: ['tasks', 'client'] },
        (old) =>
          old?.map((t) =>
            t.id === id ? { ...t, status: 'CANCELLED' as const } : t
          )
      );

      return { previousList };
    },
    onError: (error, _id, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(['tasks', 'list'], context.previousList);
      }
      toast.error('Ошибка отмены', { description: getErrorMessage(error) });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Задача отменена');
    },
  });
}
